import { paymentSettlement, paymentStatus, paymentmethods } from 'rest/urls'
import { POST } from 'rest/request'

/**
 * Jedno wejście do rozliczenia płatności - wspólne dla koszyka (containers/Payment)
 * i opłacania faktur (components/Payments), dla wszystkich metod i operatorów.
 *
 * Reguła po odpowiedzi /payment/settlement jest jedna:
 *   errors[] -> pokaż błąd | url -> przekieruj | inaczej -> odpytuj o status.
 */

const POLL_INTERVAL = 3_000
const POLL_TIMEOUT = 10 * 60 * 1_000

export type SettleResultType = 'success' | 'failure' | 'pending' | 'redirect' | 'bankTransfer' | 'error'

export interface SettleResult {
  type: SettleResultType
  remotePaymentId?: string
  /** adres bramki albo 3DS - UWAGA: backend nazywa to pole `url` */
  url?: string
  bankTransfer?: any
  /** gotowy komunikat z backendu (errors[0]) albo kod błędu frontu */
  message?: string
  /** true = błąd HTTP, globalny popup błędu już się pokazał - nie dubluj komunikatu */
  handled?: boolean
  response?: any
}

export interface SettleOptions {
  /** false = zwróć `pending` bez czekania (Apple Pay: arkusz nie doczeka pollingu) */
  poll?: boolean
  /** przerwanie odpytywania, np. gdy użytkownik zamknął popup */
  isActive?: () => boolean
  /** wołane gdy wpłata przyjęta, ale czekamy na potwierdzenie (INIT/PENDING) */
  onPending?: (remotePaymentId: string) => void
  /** po tym czasie przestajemy pytać i zwracamy `pending` (domyślnie 10 min) */
  timeoutMs?: number
}

/**
 * Czy po tym wyniku ekran nadrzędny sam gdzieś nawiguje (bramka, 3DS, ekran statusu)?
 * Popup NIE może się wtedy zamykać przez goBack() - skok w historii w tym samym ticku
 * anuluje rozpoczętą nawigację i użytkownik zostaje na miejscu.
 */
export const isNavigatingResult = (result?: SettleResult) =>
  result?.type === 'redirect' || result?.type === 'pending'

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

/** POST z rest/request odrzuca stringiem z JSON-em odpowiedzi - wyciągamy z niego komunikat */
const toMessage = (error: unknown): string => {
  const raw = typeof error === 'string' ? error : error instanceof Error ? error.message : ''
  if (!raw) return ''
  try {
    const parsed = JSON.parse(raw)
    return parsed?.errors?.[0] || parsed?.message || ''
  } catch (_) {
    return raw
  }
}

/** Odpytuje o status wpłaty do SUCCESS/FAILURE. Po limicie czasu zwraca `pending`. */
export const awaitStatus = async (remotePaymentId: string, options: SettleOptions = {}): Promise<SettleResult> => {
  const { isActive, timeoutMs = POLL_TIMEOUT } = options
  const started = Date.now()

  for (; ;) {
    await wait(POLL_INTERVAL)
    if (isActive && !isActive()) return { type: 'pending', remotePaymentId }

    let response: any
    try {
      response = await POST(paymentStatus, { body: { remotePaymentId } })
    } catch (error) {
      return { type: 'error', remotePaymentId, message: toMessage(error), handled: true }
    }

    if (response?.status === 'SUCCESS') return { type: 'success', remotePaymentId }
    if (response?.status === 'FAILURE') {
      return { type: 'failure', remotePaymentId, message: response?.errors?.[0] }
    }
    if (Date.now() - started > timeoutMs) return { type: 'pending', remotePaymentId }
  }
}

const resolveResponse = async (response: any, options: SettleOptions): Promise<SettleResult> => {
  if (!response || typeof response !== 'object') return { type: 'error', response }
  if (response.errors?.length) return { type: 'error', message: response.errors[0], response }
  if (response.url) return { type: 'redirect', url: response.url, remotePaymentId: response.remotePaymentId, response }
  if (response.bankTransfer) {
    return { type: 'bankTransfer', bankTransfer: response.bankTransfer, remotePaymentId: response.remotePaymentId, response }
  }
  if (!response.remotePaymentId) return { type: 'error', response }

  if (response.status === 'SUCCESS') return { type: 'success', remotePaymentId: response.remotePaymentId, response }
  if (response.status === 'FAILURE') {
    return { type: 'failure', remotePaymentId: response.remotePaymentId, message: response.errors?.[0], response }
  }

  // INIT | PENDING
  if (options.poll === false) return { type: 'pending', remotePaymentId: response.remotePaymentId, response }
  options.onPending?.(response.remotePaymentId)
  return awaitStatus(response.remotePaymentId, options)
}

const post = async (url: string, body: any, options: SettleOptions): Promise<SettleResult> => {
  if (!body) return { type: 'error' }

  let response: any
  try {
    response = await POST(url, { body })
  } catch (error) {
    // popup błędu pokazał już rest/request - stąd handled: true
    return { type: 'error', message: toMessage(error), handled: true }
  }

  return resolveResponse(response, options)
}

export const settle = (body: any, options: SettleOptions = {}): Promise<SettleResult> =>
  post(paymentSettlement, body, options)

/** Sygnatura wysyłki używana przez BlikPopup / CardPopup - domyślnie `settle`. */
export type SubmitFn = (body: any, options?: SettleOptions) => Promise<SettleResult>

/**
 * Dodanie metody bez faktury (kontrakt §5.2): wpłata weryfikacyjna zwracana automatycznie.
 * POST /user/paymentmethods { companyId, method, consent: true, blikCode? | cardToken? | walletToken? }
 * Odpowiedź ma kształt rozliczenia, więc reguła jest ta sama: errors -> url -> odpytywanie.
 */
export const registerPaymentMethod = (body: any, options: SettleOptions = {}): Promise<SettleResult> =>
  post(paymentmethods, body, options)
